/**
 * Explanations that appear when asked for.
 *
 * The first build printed a paragraph under every slider. It was accurate and
 * exhausting: the rail was mostly prose, and the prose pushed the controls that
 * matter below the fold. The text is worth keeping — just not on screen all the
 * time. It lives here, one hover or focus away, and the panels stay a list of
 * controls.
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const OPEN_DELAY_MS = 380;
const EDGE_MARGIN = 10;

interface TipProps {
  /** Bold first line. Usually the control's own name. */
  title?: string;
  /** The explanation. Keep it to one or two sentences. */
  body: ReactNode;
  children: ReactNode;
  /** Wrapper element, so a tip can sit around a label without breaking layout. */
  as?: 'span' | 'div';
  className?: string;
}

export function Tip({ title, body, children, as = 'span', className }: TipProps) {
  const [box, setBox] = useState<{ x: number; y: number; above: boolean } | null>(null);
  const [shown, setShown] = useState(false);
  const anchorRef = useRef<HTMLElement>(null);
  const timerRef = useRef(0);
  const id = useId();

  const close = useCallback(() => {
    window.clearTimeout(timerRef.current);
    setShown(false);
    setBox(null);
  }, []);

  const open = useCallback(
    (immediate: boolean) => {
      window.clearTimeout(timerRef.current);
      const show = () => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        const rect = anchor.getBoundingClientRect();
        // Flip above when there is no room below, so a tip on the last control
        // in the rail is not clipped by the filmstrip.
        const above = rect.bottom + 120 > window.innerHeight;
        setBox({
          x: Math.min(Math.max(rect.left, EDGE_MARGIN), window.innerWidth - 270),
          y: above ? rect.top - 8 : rect.bottom + 8,
          above,
        });
        setShown(true);
      };
      if (immediate) show();
      else timerRef.current = window.setTimeout(show, OPEN_DELAY_MS);
    },
    [],
  );

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (!shown) return;
    const dismiss = () => close();
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    window.addEventListener('pointerdown', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('pointerdown', dismiss);
    };
  }, [shown, close]);

  const Wrapper = as;

  return (
    <>
      <Wrapper
        ref={anchorRef as never}
        className={className ? `tip ${className}` : 'tip'}
        aria-describedby={shown ? id : undefined}
        onPointerEnter={() => open(false)}
        onPointerLeave={close}
        onFocusCapture={() => open(true)}
        onBlurCapture={close}
      >
        {children}
      </Wrapper>
      {shown && box
        ? createPortal(
            <div
              id={id}
              role="tooltip"
              className="tip-bubble in"
              style={{
                left: box.x,
                top: box.above ? undefined : box.y,
                bottom: box.above ? window.innerHeight - box.y : undefined,
              }}
            >
              {title ? <b>{title}</b> : null}
              <span>{body}</span>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
